import { Fragment, type FC } from "react";
import { FormItemName } from "@root/shared/components/FormItemName";
import { InformationText } from "@root/shared/components/InformationText";
import { RadioWithLabel } from "@userFrontend/features/promotion/components/RadioWithLabel";
import { StepCassette } from "../StepCassette";
import * as styles from "./styles.css";
import { headingEmphasis } from "../../styles.css";

type CredentialStatusOption = {
  willFlag: boolean;
  label: string;
};

type CredentialItem = {
  userCredentialItemId: number;
  name: string;
  credentialStatusOptions: CredentialStatusOption[];
};

type CredentialResponse = {
  requiredCredentials: CredentialItem[];
  optionalCredentials: CredentialItem[];
};

type Props = {
  jobTypeName: string;
  experienceName: string;
  credentials: CredentialResponse | null;
  selectedCredentials: Record<string, { willFlag: boolean | null }>;
  onChangeCredential: (credentialId: number, willFlag: boolean) => void;
  onSelectNone: (credentialId: number) => void;
  errorMessage?: string;
};

export const Presentation: FC<Props> = ({
  jobTypeName,
  experienceName,
  credentials,
  selectedCredentials,
  onChangeCredential,
  onSelectNone,
  errorMessage,
}) => {
  return (
    <StepCassette
      jobTypeAndExperience={`${jobTypeName}/${experienceName}`}
      heading={
        <>
          <span className={headingEmphasis}>持っている資格</span>を教えてください
        </>
      }
    >
      {credentials && (
        <>
          <div className={styles.credentialItem}>
            {credentials.requiredCredentials.map((credential) => (
              <Fragment key={credential.userCredentialItemId}>
                <FormItemName labelRequirementType="required">{credential.name}</FormItemName>
                <span className={styles.radioButtonWrapper({ isOptional: false })}>
                  {credential.credentialStatusOptions.map((option) => {
                    const credentialId = String(credential.userCredentialItemId);
                    const optionValue = String(option.willFlag);
                    const uniqueId = `credential-${credentialId}-${optionValue}`;

                    return (
                      <RadioWithLabel
                        key={uniqueId}
                        id={uniqueId}
                        name="requiredCredentials"
                        label={option.label}
                        value={credentialId}
                        checked={selectedCredentials[credentialId]?.willFlag === option.willFlag}
                        onChange={() =>
                          onChangeCredential(credential.userCredentialItemId, option.willFlag)
                        }
                        hasError={!!errorMessage}
                      />
                    );
                  })}
                </span>
              </Fragment>
            ))}
            {errorMessage && (
              <InformationText isAlert text={errorMessage} className={styles.errorMessage} />
            )}
          </div>
          {credentials.optionalCredentials.map((credential) => {
            const credentialId = String(credential.userCredentialItemId);
            const isNoneSelected = selectedCredentials[credentialId]?.willFlag === null;

            return (
              <div key={credential.userCredentialItemId} className={styles.credentialItem}>
                <FormItemName labelRequirementType="optional">{credential.name}</FormItemName>
                <span className={styles.radioButtonWrapper({ isOptional: true })}>
                  {credential.credentialStatusOptions.map((option) => {
                    const optionValue = String(option.willFlag);
                    const uniqueId = `credential-${credentialId}-${optionValue}`;

                    return (
                      <span key={uniqueId} className={styles.optionalCredential}>
                        <RadioWithLabel
                          id={uniqueId}
                          name={`optionalCredential-${credentialId}`}
                          label={option.label}
                          value={credentialId}
                          checked={selectedCredentials[credentialId]?.willFlag === option.willFlag}
                          onChange={() =>
                            onChangeCredential(credential.userCredentialItemId, option.willFlag)
                          }
                        />
                      </span>
                    );
                  })}
                  <span className={styles.optionalCredential}>
                    <RadioWithLabel
                      id={`credential-${credentialId}-none`}
                      name={`optionalCredential-${credentialId}`}
                      label="なし"
                      value="none"
                      checked={isNoneSelected}
                      onChange={() => onSelectNone(credential.userCredentialItemId)}
                    />
                  </span>
                </span>
              </div>
            );
          })}
        </>
      )}
    </StepCassette>
  );
};
